import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider";
import { api } from "../lib/api";
import { useFeder } from "../lib/spring";

/* --- Symbole: 18 px, Strich 1.6, ein Satz, eine Handschrift ------------------- */

type SymbolName =
  | "bestellungen"
  | "objekte"
  | "mandanten"
  | "konfigurator"
  | "hersteller"
  | "stoffe"
  | "naehateliere"
  | "montage"
  | "kalkulation"
  | "api"
  | "swagger"
  | "abmelden"
  | "suche"
  | "chevron";

const PFADE: Record<SymbolName, ReactNode> = {
  bestellungen: (
    <>
      <path d="M3 9.5 5.2 4.5h13.6L21 9.5v9a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18.5z" />
      <path d="M3 9.5h5.5a3.5 3.5 0 0 0 7 0H21" />
    </>
  ),
  objekte: (
    <>
      <path d="M4 20V5.5A1.5 1.5 0 0 1 5.5 4h7A1.5 1.5 0 0 1 14 5.5V20" />
      <path d="M14 10h4.5A1.5 1.5 0 0 1 20 11.5V20M2.5 20h19" />
      <path d="M7 8h2M7 12h2M7 16h2M17 14h0M17 17h0" />
    </>
  ),
  mandanten: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
      <path d="M16 4.5a3.5 3.5 0 0 1 0 7M21 20c0-2.8-1.9-5.1-4.5-5.8" />
    </>
  ),
  konfigurator: (
    <>
      <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
      <circle cx="16" cy="7" r="2.2" />
      <circle cx="10" cy="17" r="2.2" />
    </>
  ),
  hersteller: (
    <>
      <path d="M3 20V9l5 3V9l5 3V9l5 3V6.5A1.5 1.5 0 0 1 19.5 5H21v15z" />
      <path d="M2.5 20h19M7 16h2M11 16h2M15 16h2" />
    </>
  ),
  stoffe: (
    <>
      <path d="m12 3 8.5 4.5L12 12 3.5 7.5z" />
      <path d="m3.5 12 8.5 4.5 8.5-4.5M3.5 16.5 12 21l8.5-4.5" />
    </>
  ),
  naehateliere: (
    <>
      <circle cx="6.5" cy="6.5" r="2.75" />
      <circle cx="6.5" cy="17.5" r="2.75" />
      <path d="m8.7 8.4 11.3 7.1M8.7 15.6 20 8.5" />
    </>
  ),
  montage: (
    <>
      <path d="M14.5 6.5a4 4 0 0 0 5 5L9 22l-3-3z" />
      <path d="m15 3.5 1.5 1.5M19.5 8 21 9.5" />
    </>
  ),
  kalkulation: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M8.5 7h7M8.5 11.5h0M12 11.5h0M15.5 11.5h0M8.5 15h0M12 15h0M15.5 15h0M8.5 18.5h7" />
    </>
  ),
  api: (
    <>
      <path d="m8 7-5 5 5 5M16 7l5 5-5 5M13.5 4l-3 16" />
    </>
  ),
  swagger: (
    <>
      <path d="M14 4h6v6M20 4l-9 9" />
      <path d="M18 13.5V18a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4.5" />
    </>
  ),
  abmelden: (
    <>
      <path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4" />
      <path d="m15 8 4 4-4 4M19 12H9" />
    </>
  ),
  suche: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4.5 4.5" />
    </>
  ),
  chevron: <path d="m9 6 6 6-6 6" />,
};

function Symbol({ name }: { name: SymbolName }) {
  return (
    <svg className="sl-symbol" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {PFADE[name]}
    </svg>
  );
}

/* --- Navigation nach Aufgaben gruppiert – benannt nach dem, was dahinter liegt -- */

type Eintrag = { to: string; label: string; symbol: SymbolName; end?: boolean; extern?: boolean };
type Bereich = { titel: string; eintraege: Eintrag[]; einklappbar?: boolean };

const BEREICHE: Bereich[] = [
  {
    titel: "Vertrieb",
    eintraege: [
      { to: "/bestellungen", label: "Bestellungen", symbol: "bestellungen" },
      { to: "/objekte", label: "Objekte", symbol: "objekte" },
      { to: "/", label: "Mandanten", symbol: "mandanten", end: true },
    ],
  },
  {
    titel: "Katalog",
    eintraege: [
      { to: "/konfigurator", label: "Konfigurator", symbol: "konfigurator" },
      { to: "/hersteller", label: "Hersteller", symbol: "hersteller" },
      { to: "/stoffe", label: "Stoffe", symbol: "stoffe" },
      { to: "/naehateliere", label: "Nähateliere", symbol: "naehateliere" },
      { to: "/montageeditor", label: "Montageeditor", symbol: "montage" },
      { to: "/kalkulation", label: "Kalkulation", symbol: "kalkulation" },
    ],
  },
  {
    titel: "Entwickler",
    einklappbar: true,
    eintraege: [
      { to: "/api-docs", label: "API-Dokumentation", symbol: "api" },
      { to: "/swagger", label: "Swagger", symbol: "swagger", extern: true },
    ],
  },
];

const EINGEKLAPPT_SCHLUESSEL = "ocg.seitenleiste.eingeklappt";

function liesEingeklappt(): Set<string> {
  try {
    const roh = localStorage.getItem(EINGEKLAPPT_SCHLUESSEL);
    return new Set(roh ? (JSON.parse(roh) as string[]) : ["Entwickler"]);
  } catch {
    return new Set(["Entwickler"]);
  }
}

/** Ungelesene Bestellungen – bei jedem Seitenwechsel neu, damit der Zähler stimmt. */
function useUngelesen(pfad: string): number {
  const [anzahl, setAnzahl] = useState(0);
  useEffect(() => {
    let aktiv = true;
    void api.api.admin.bestellungen
      .$get()
      .then(async (res) => {
        if (!res.ok || !aktiv) return;
        const daten = await res.json();
        if ("bestellungen" in daten) setAnzahl(daten.bestellungen.filter((b) => !b.gelesenAm).length);
      })
      .catch(() => undefined);
    return () => {
      aktiv = false;
    };
  }, [pfad]);
  return anzahl;
}

/** Erster Pfadabschnitt – „/stoffe/neu“ gehört zu „Stoffe“, „/“ zu Mandanten. */
function aktiverEintrag(pfad: string): string {
  if (pfad === "/" || pfad.startsWith("/tenants")) return "/";
  const kopf = "/" + pfad.split("/")[1];
  return BEREICHE.flatMap((b) => b.eintraege).some((e) => e.to === kopf) ? kopf : "";
}

/* --- Layout ---------------------------------------------------------------------- */

export function AppLayout() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const ungelesen = useUngelesen(location.pathname);

  const [eingeklappt, setEingeklappt] = useState(liesEingeklappt);
  const [suche, setSuche] = useState<string | null>(null);
  const sucheRef = useRef<HTMLInputElement>(null);

  function klappe(titel: string) {
    setEingeklappt((alt) => {
      const neu = new Set(alt);
      if (neu.has(titel)) neu.delete(titel);
      else neu.add(titel);
      try {
        localStorage.setItem(EINGEKLAPPT_SCHLUESSEL, JSON.stringify([...neu]));
      } catch {
        /* privates Fenster – dann eben nicht gemerkt */
      }
      return neu;
    });
  }

  // Suche: das Feld erscheint, der Fokus geht sofort hinein; Escape schließt.
  useEffect(() => {
    if (suche !== null) sucheRef.current?.focus();
  }, [suche]);
  useEffect(() => {
    setSuche(null);
  }, [location.pathname]);

  const aktiv = aktiverEintrag(location.pathname);

  /* Die Markierung ist ein Element, das per Feder zum aktiven Eintrag gleitet.
     Wechselt der Nutzer während der Bewegung erneut, biegt sie ab, statt zu springen. */
  const nav = useRef<HTMLElement>(null);
  const [mass, setMass] = useState({ y: 0, hoehe: 0 });
  const [bereit, setBereit] = useState(false);
  const messe = () => {
    const el = nav.current?.querySelector<HTMLElement>(`[data-to="${aktiv}"]`);
    if (!el || !nav.current) {
      setMass((m) => ({ ...m, hoehe: 0 }));
      return;
    }
    setMass({ y: el.offsetTop, hoehe: el.offsetHeight });
  };
  useLayoutEffect(messe, [aktiv, eingeklappt, suche]);
  useEffect(() => {
    const ro = new ResizeObserver(messe);
    if (nav.current) ro.observe(nav.current);
    setBereit(true);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const y = useFeder(mass.y, { response: 0.32, damping: 1 });
  const hoehe = useFeder(mass.hoehe, { response: 0.32, damping: 1 });

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  const filter = suche?.trim().toLocaleLowerCase("de") ?? "";
  const gefiltert = filter ? BEREICHE.flatMap((b) => b.eintraege).filter((e) => e.label.toLocaleLowerCase("de").includes(filter)) : null;

  const eintrag = (e: Eintrag) => {
    const inhalt = (
      <>
        <Symbol name={e.symbol} />
        <span className="sl-eintrag__label">{e.label}</span>
        {e.to === "/bestellungen" && ungelesen > 0 && (
          <span className="app__zaehler" aria-label={`${ungelesen} ungelesen`}>
            {ungelesen}
          </span>
        )}
      </>
    );
    if (e.extern) {
      return (
        <a key={e.to} className="sl-eintrag" href={e.to} target="_blank" rel="noreferrer" data-to={e.to}>
          {inhalt}
        </a>
      );
    }
    return (
      <NavLink key={e.to} to={e.to} end={e.end} className="sl-eintrag" data-to={e.to}>
        {inhalt}
      </NavLink>
    );
  };

  return (
    <div className="app">
      <aside className="app__seitenleiste">
        <div className="sl-kopf">
          <div className="sl-konto" title="OrderCity Großhandel">
            <span className="app__logo" aria-hidden="true">
              OC
            </span>
            <span className="sl-konto__name">OrderCity</span>
          </div>
          <button
            type="button"
            className={`sl-symbolknopf${suche !== null ? " sl-symbolknopf--aktiv" : ""}`}
            aria-label={suche === null ? "Seite suchen" : "Suche schließen"}
            aria-pressed={suche !== null}
            onPointerDown={(ev) => ev.preventDefault()}
            onClick={() => setSuche((s) => (s === null ? "" : null))}
          >
            <Symbol name="suche" />
          </button>
        </div>

        {suche !== null && (
          <div className="sl-suche">
            <input
              ref={sucheRef}
              type="search"
              value={suche}
              placeholder="Seite suchen"
              aria-label="Seite suchen"
              onChange={(ev) => setSuche(ev.target.value)}
              onKeyDown={(ev) => {
                if (ev.key === "Escape") setSuche(null);
                if (ev.key === "Enter" && gefiltert?.[0] && !gefiltert[0].extern) navigate(gefiltert[0].to);
              }}
            />
          </div>
        )}

        <nav className="app__nav" aria-label="Hauptnavigation" ref={nav}>
          {bereit && mass.hoehe > 0 && (
            <span className="sl-marke" aria-hidden="true" style={{ transform: `translateY(${y}px)`, height: hoehe }} />
          )}

          {gefiltert ? (
            <div className="sl-bereich">
              {gefiltert.length === 0 ? <span className="sl-bereich__leer">Nichts gefunden.</span> : gefiltert.map(eintrag)}
            </div>
          ) : (
            BEREICHE.map((b) => {
              const zu = b.einklappbar && eingeklappt.has(b.titel);
              return (
                <div key={b.titel} className={`sl-bereich${zu ? " sl-bereich--zu" : ""}`}>
                  {b.einklappbar ? (
                    <button type="button" className="sl-bereich__titel sl-bereich__titel--knopf" aria-expanded={!zu} onClick={() => klappe(b.titel)}>
                      {b.titel}
                      <Symbol name="chevron" />
                    </button>
                  ) : (
                    <span className="sl-bereich__titel">{b.titel}</span>
                  )}
                  <div className="sl-bereich__inhalt">
                    <div className="sl-bereich__liste">{b.eintraege.map(eintrag)}</div>
                  </div>
                </div>
              );
            })
          )}
        </nav>

        <div className="sl-fuss">
          <button type="button" className="sl-eintrag sl-eintrag--knopf" onClick={() => void handleLogout()}>
            <Symbol name="abmelden" />
            <span className="sl-eintrag__label">Abmelden</span>
          </button>
        </div>
      </aside>

      <div className="app__rahmen">
        <div className="app__inhalt">
          {/* Key auf dem Pfad: Jeder Seitenwechsel blendet den Inhalt kurz ein. */}
          <main className="app__main" key={location.pathname}>
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
